// @ts-strict-ignore
/*
  TODO:
  - [x] Phase 1: Only basic data types with default flattening operators
  - [x] Phase 2: Complex data types and nested recursion
  - [ ] Phase 3: All flattening operators
        [ ] directExport?: boolean | null; // Very special instruction which takes the input value and exports it directly into the profile export data, should only be used in very specific edge cases where there is only one input (as it any other input values will be irrelevant) and its value is desired to be the actual exported value. Note this should only be used with "string" inputs set with the stringToJSON flattening operator (as the directly exported input value must be JSON).
        [ ] arrayFlatteningStyle?: ProfileElementArrayFlatteningStyleType | null; // Optional, only for "array" types, provides special instructions for flattening array data into the export format
            [ ] "normal" // (Default) Arrays are kept pure and intact as a value of an array of objects
            [ ] "singlearray" // (Note: only allowed for arrays with a single input) Each value of the array's objects are reduced to a single array of the values
            [ ] "nullproperties" // (Note: only allowed for arrays with a single input) Each label/value of the array is instead reduced to a property on the parent object with the value used as the property name and a null value assignment
            [ ] "keyvaluepairs" // Special array flattening operator that creates key/value properties on the parent of the array input for each array record. When this type is used, the 'keyValuePairs' property must be defined to configure what array input is the key and value respectively.
            [ ] "nestedarray"; // Should only be used for an array input that has one and only one child array as an input, in that case when using this the child array values are a direct decendent of the parent array and not a key/value pair child of it
        [ ] keyValuePairs?: ProfileElementArrayFlatteningKeyValuePairs | null; // Required when arrayFlatteningStyle is of type 'keyvaluepairs' to configure what array input is the key and value respectively
        [ ] stringToJSON?: boolean | null; // Optional, only applies to "string" types, when set to true treats the string as json when exporting the value
*/
import { Injectable } from "@angular/core";
import { ExpertProfileModels } from "src/app/shared/models/shared.models.types";
import { ProfileElementScreenInput } from "../models/profile-element.model";
import { ProfileModels } from "../models/profile.models.types";

@Injectable({ providedIn: "root" })
export class ExpertImportService {
  importIntoSchema(jsonSource: any, schema: ProfileModels.ProfileElementSchema): ExpertProfileModels.ExpertModeProfileElementDto[] {
    //console.clear();
    //this.logService.log("importIntoSchema", jsonSource, schema);

    const elements: ExpertProfileModels.ExpertModeProfileElementDto[] = [];
    if (jsonSource) {
      const sourceElements: any = Object.values(jsonSource);

      for (const sourceElement of sourceElements) {
        const sourceElementKeys = Object.keys(sourceElement);
        const sourceElementValues = Object.values(sourceElement);

        if (sourceElementKeys && sourceElementKeys.length > 0) {
          //this.logService.log(`processing object key '${sourceElementKeys[0]}' for matches`);

          for (const schemaElement of schema.elements) {
            // If we can resolve this element to an element defined in the schema, create an instance of this element and try to populate all the values we have for it
            if (schemaElement.elementName === sourceElementKeys[0]) {
              //this.logService.log(`found match for element '${sourceElementKeys[0]}' in the schema`, schemaElement);

              const newValues: ExpertProfileModels.ExpertModeProfileElementValueDto[] = [];

              // Iterate through all values from the source for this element and push any inputs that match into the values array
              for (const value of sourceElementValues) {
                const properties = Object.keys(value);
                const values = Object.values(value);

                if (properties && properties.length > 0 && values && values.length > 0) {
                  for (let i = 0; i < properties.length; i++) {
                    //this.logService.log(`processing property '${properties[i]}'`, values[i]);

                    for (const screen of schemaElement.screens) {
                      for (const input of screen.inputs) {
                        if (input.propertyName === properties[i]) {
                          //this.logService.log(`found a match for input '${properties[i]}' on a screen`, input);

                          if (input.type === "array" || input.type === "object") {
                            //console.warn(`special processing for '${input.type}' type`, input, values[i]);
                            let nestedValue: any[] = [];

                            if (input.type === "array") {
                              nestedValue = this.getNestedValues(input, values[i]);
                            } else if (input.type === "object") {
                              nestedValue = this.getNestedValues(input, [values[i]]);
                            }

                            newValues.push({
                              key: input.key,
                              instanceId: crypto.randomUUID(),
                              propertyName: input.propertyName,
                              value: nestedValue
                            });
                          } else {
                            //console.info(`basic import of not complex '${input.type}' data type`, input, values[i]);

                            newValues.push({
                              key: input.key,
                              instanceId: crypto.randomUUID(),
                              propertyName: input.propertyName,
                              value: values[i]
                            });
                          }

                          break;
                        }
                      }
                    }
                  }
                }
              }

              elements.push({
                key: schemaElement.key,
                instanceId: crypto.randomUUID(),
                elementName: schemaElement.elementName,
                values: newValues
              });

              break;
            }
          }
        }
      }
    }

    //this.logService.log("elements", elements);

    return elements;
  }

  getNestedValues(nestedInput: ProfileElementScreenInput, nestedValues: any[]): any[] {
    const values = [];

    //this.logService.log("getNestedValues", nestedInput, nestedValues);

    if (nestedValues && JSON.stringify(nestedValues) !== "[{}]") {
      for (const value of nestedValues) {
        const childValue = [];

        for (const input of nestedInput.inputs) {
          if (input.propertyName) {
            if (input.type === "array" || input.type === "object") {
              //console.warn(`special nested processing for '${input.type}' type`, input);

              let nestedValue: any[] = [];

              if (input.type === "array") {
                nestedValue = this.getNestedValues(input, value[input.propertyName]);
              } else if (input.type === "object") {
                nestedValue = this.getNestedValues(input, [value[input.propertyName]]);
              }

              //this.logService.log(nestedValue);

              childValue.push({
                key: input.key,
                instanceId: crypto.randomUUID(),
                propertyName: input.propertyName,
                value: nestedValue
              });
            } else {
              //console.info(`basic import of not complex '${input.type}' data type`, input, value);

              if (typeof value[input.propertyName] !== "undefined") {
                //this.logService.log(value[input.propertyName]);

                childValue.push({
                  key: input.key,
                  instanceId: crypto.randomUUID(),
                  propertyName: input.propertyName,
                  value: value[input.propertyName]
                });
              }
            }
          }
        }

        values.push(childValue);
      }
    }

    return values;
  }
}
